import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "customer", "shipping", "billing", "sameAsShipping",
        // Inline "+ Add new address" disclosure form
        "newAddressPanel", "newAddressType",
        "newLine1", "newLine2", "newCity", "newState", "newZip", "newCountry",
        "newAddressError", "newAddressSubmit"
    ]
    static values = { url: String }

    connect() {
        this.initialLoad = true
        // Check initial state (e.g. if editing or re-rendering)
        this.toggleSameAsShipping()
    }

    fetchAddresses() {
        const customerId = this.customerTarget.value
        if (!customerId) return

        const url = `/customers/${customerId}/addresses`

        fetch(url, {
            headers: {
                "Accept": "application/json"
            }
        })
            .then(response => response.json())
            .then(data => {
                this.updateSelect(this.shippingTarget, data.shipping)
                this.updateSelect(this.billingTarget, data.billing)

                // Re-apply sync logic after options are updated
                this.syncShippingToBilling()
            })
            .catch(error => console.error("Error fetching addresses:", error))
    }

    updateSelect(selectElement, addresses) {
        if (!selectElement) return

        const currentSelection = selectElement.value
        selectElement.innerHTML = ""

        const prompt = document.createElement("option")
        prompt.text = "Select Address"
        prompt.value = ""
        selectElement.add(prompt)

        addresses.forEach(address => {
            const option = document.createElement("option")
            option.value = address.id
            const typeLabel = address.address_type ? `[${address.address_type.toUpperCase()}] ` : ""
            const parts = [address.address_line1, address.city, address.state].filter(Boolean)
            option.text = typeLabel + parts.join(", ")
            selectElement.add(option)
        })

        if (currentSelection && Array.from(selectElement.options).some(o => o.value == currentSelection)) {
            selectElement.value = currentSelection
        }
    }

    change() {
        this.initialLoad = false
        this.fetchAddresses()
    }

    toggleSameAsShipping() {
        if (!this.hasSameAsShippingTarget) return

        const isChecked = this.sameAsShippingTarget.checked
        this.billingTarget.disabled = isChecked

        if (isChecked) {
            this.syncShippingToBilling()
        }
    }

    syncShippingToBilling() {
        if (this.hasSameAsShippingTarget && this.sameAsShippingTarget.checked) {
            this.billingTarget.value = this.shippingTarget.value
        }
    }

    // -------------------------------------------------------------------
    // Inline "+ Add new address" flow
    // -------------------------------------------------------------------
    // The order form previously only let you select from existing
    // customer addresses; if a customer had none, you had to leave the
    // order half-saved, go to the customer edit page, add an address,
    // and come back. This adds an inline disclosure form right under
    // the dropdowns that POSTs to /customers/:id/addresses and slots
    // the newly-created Address into the relevant dropdown without a
    // navigation.

    showNewAddress(event) {
        event.preventDefault()
        if (!this.hasNewAddressPanelTarget) return
        // Default the radio (shipping vs billing) based on which "+ Add"
        // link the user clicked — stored on the trigger's data-type attr.
        const type = event.currentTarget.dataset.type || "shipping"
        if (this.hasNewAddressTypeTarget) {
            this.newAddressTypeTargets.forEach(radio => {
                radio.checked = (radio.value === type)
            })
        }
        this.newAddressPanelTarget.hidden = false
        if (this.hasNewLine1Target) this.newLine1Target.focus()
    }

    hideNewAddress(event) {
        if (event) event.preventDefault()
        if (this.hasNewAddressPanelTarget) this.newAddressPanelTarget.hidden = true
        if (this.hasNewAddressErrorTarget) this.newAddressErrorTarget.textContent = ""
    }

    submitNewAddress(event) {
        event.preventDefault()
        const customerId = this.customerTarget.value
        if (!customerId) {
            this.showAddressError("Pick a customer before adding an address.")
            return
        }

        const checkedType = this.newAddressTypeTargets.find(r => r.checked)?.value || "shipping"
        const payload = {
            address: {
                address_type:  checkedType,
                address_line1: this.newLine1Target?.value || "",
                address_line2: this.newLine2Target?.value || "",
                city:          this.newCityTarget?.value  || "",
                state:         this.newStateTarget?.value || "",
                zip:           this.newZipTarget?.value   || "",
                country:       this.newCountryTarget?.value || "US"
            }
        }

        // Block double-submit
        if (this.hasNewAddressSubmitTarget) this.newAddressSubmitTarget.disabled = true

        const csrf = document.querySelector('meta[name="csrf-token"]')?.content
        fetch(`/customers/${customerId}/addresses`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": csrf || ""
            },
            body: JSON.stringify(payload)
        })
            .then(async (res) => {
                const data = await res.json().catch(() => ({}))
                if (!res.ok || !data.success) {
                    this.showAddressError((data.errors || ["Could not save address."]).join(", "))
                    return
                }
                // Re-render dropdowns with the latest server-side list,
                // then auto-select the newly-created address on the right
                // dropdown. fetchAddresses is async; we wait a tick before
                // selecting so the options exist when we assign .value.
                this.fetchAddresses()
                setTimeout(() => {
                    const targetSelect = checkedType === "billing" ? this.billingTarget : this.shippingTarget
                    if (targetSelect && Array.from(targetSelect.options).some(o => o.value == data.address.id)) {
                        targetSelect.value = data.address.id
                    }
                }, 400)
                this.clearAddressForm()
                this.hideNewAddress()
            })
            .catch(() => this.showAddressError("Network error — please try again."))
            .finally(() => {
                if (this.hasNewAddressSubmitTarget) this.newAddressSubmitTarget.disabled = false
            })
    }

    showAddressError(msg) {
        if (this.hasNewAddressErrorTarget) this.newAddressErrorTarget.textContent = msg
    }

    clearAddressForm() {
        ["newLine1", "newLine2", "newCity", "newState", "newZip"].forEach(name => {
            const cap = name.charAt(0).toUpperCase() + name.slice(1)
            if (this[`has${cap}Target`]) this[`${name}Target`].value = ""
        })
    }
}
